import {Helmet} from '@dr.pogodin/react-helmet';
import {showNotification} from 'platform/components';

import {defaultTo} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {
  useGetCostCorrectionQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  createHeaderActions,
  DetailTemplate,
  DetailTemplateHeader,
  printPDF,
  useFileNotification,
  handleApiError,
  NavigationItem,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {composePath, generateHashFromObjects, useNavigate} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {Overview} from './(sections)/Overview/Overview';

export function CostCorrectionDetail() {
  const {costCorrectionId} = useWarehouseParams();

  const fileNotification = useFileNotification({onTimeout: () => showNotification.error()});

  const navigate = useNavigate();

  const {
    data: costCorrection,
    isLoading: isCostCorrectionLoading,
    isFetching: isCostCorrectionFetching,
    isError: hasCostCorrectionError,
  } = useGetCostCorrectionQuery({costCorrectionId}, {skip: isNilOrEmpty(costCorrectionId)});

  const {
    data: costCorrectionActions,
    isFetching: isCostCorrectionActionsFetching,
    isError: hasCostCorrectionActionsError,
  } = useGetWarehouseHeaderDynamicActionsQuery(
    {id: costCorrectionId, resource: 'cost-correction'},
    {skip: isNilOrEmpty(costCorrectionId)}
  );

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-issue-note-correction', contextId: costCorrectionId},
    {skip: !costCorrectionId}
  );

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'warehouse-issue-note-correction',
    contextId: costCorrectionId,
  });

  const [putCostCorrectionAction, {isLoading: isPutCostCorrectionActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'prntIssueNoteCostCorrection') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: costCorrectionId,
          originEntityPrintoutType: 'prntIssueNoteCostCorrection',
        },
      })
        .unwrap()
        .then((data) => {
          const fileId = data?.printout?.fileId;
          const pdfUrl = data?.printout?.pdfUrl;

          if (fileId) {
            fileNotification.registerFile(fileId, {
              onSuccess: () => {
                if (pdfUrl) {
                  printPDF(pdfUrl);
                } else {
                  showNotification.error();
                }
              },
              onError: () => showNotification.error(),
            });
          } else {
            showNotification.error();
          }
        })
        .then(refreshDocumentsCount)
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.costCorrectionDetailDocuments, {
              params: {id: costCorrectionId},
            })
          );
        })
        .catch(handleApiError);
    }

    putCostCorrectionAction({
      id: costCorrectionId,
      resource: 'cost-correction',
      body: {actionKey},
    })
      .unwrap()
      .then(refreshDocumentsCount)
      .catch(handleApiError);
  };

  const headerActions = createHeaderActions({
    actions: costCorrectionActions?.actions,
    callback: handleActionClick,
    isLoading: isPutCostCorrectionActionLoading || isPostPrintoutOnDemandLoading,
  });

  const isLoading = isCostCorrectionLoading || isCostCorrectionActionsFetching;

  const isError = hasCostCorrectionError || hasCostCorrectionActionsError;

  const header: DetailTemplateHeader = {
    title: `${i18n.t('entity.warehouse.labels.costCorrection')} ${defaultTo('', costCorrection?.costCorrectionInfo.number)}`,
    isEntityLoading: isCostCorrectionFetching,
    actions: headerActions,
  };

  const navigation: NavigationItem[] = [
    {
      id: 'overview',
      label: i18n.t('general.labels.overview'),
      href: composePath(warehouseRoutes.costCorrectionDetailOverview, {
        params: {id: costCorrectionId},
      }),
      content: (
        <Overview
          costCorrection={costCorrection}
          isCostCorrectionLoading={isCostCorrectionLoading}
          data-testid={testIds.warehouse.costCorrectionDetailOverview('section.overview')}
        />
      ),
      'data-testid': testIds.warehouse.costCorrectionDetailOverview('navigation.overview'),
    },
    {
      id: 'documents',
      label: i18n.t('general.labels.documents'),
      href: composePath(warehouseRoutes.costCorrectionDetailDocuments, {
        params: {id: costCorrectionId},
      }),
      content: (
        <Documents
          onDocumentAction={refreshDocumentsCount}
          data-testid={testIds.warehouse.costCorrectionDetailOverview('section.documents')}
        />
      ),
      badge: documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.costCorrectionDetailOverview('navigation.documents'),
    },
  ];

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.costCorrectionDetail')} />
      <DetailTemplate
        key={generateHashFromObjects(costCorrection)}
        isLoading={isLoading}
        isError={isError}
        isLocked={isPutCostCorrectionActionLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.costCorrectionDetailOverview('page')}
      />
    </>
  );
}
